/**
 * كتالوج مستندات الموظّف.
 *
 * ورقةٌ في الملفّ: نوعُها ورقمُها وتاريخُ انتهائها وصورتُها.
 *
 * ## ولماذا لا يكفي المرفق
 *
 * النظامُ يرفع الملفّاتِ بالفعل — لكنّ مجلّدًا لا يُجيب سؤالين تُسأل بهما
 * إدارةُ الموارد البشرية كلَّ شهر: مَن ينقصه مستندٌ مطلوب، ومَن ينتهي
 * مستندُه قريبًا. والعمودان — النوعُ والانتهاء — هما ما يُنتجهما.
 *
 * ## وما لا يفعله هذا الباب
 *
 * لا يتحقّق من صحّة ورقةٍ ولا يقرأ ما فيها. «رُوجعت» واقعةٌ يسجّلها من
 * راجعها بعينه — لا تحقُّقٌ آليّ، وادّعاءُ غير ذلك يجعل ملفًّا مزوَّرًا
 * يبدو معتمَدًا من النظام.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   أنواعُ المستندات
   ========================================================================== */

export const DOC_KINDS = [
  'NATIONAL_ID',
  'PASSPORT',
  'WORK_PERMIT',
  'RESIDENCY',
  'QUALIFICATION',
  'EXPERIENCE',
  'MEDICAL',
  'CRIMINAL_RECORD',
  'MILITARY',
  'INSURANCE_FORM',
  'BANK',
  'PHOTO',
  'OTHER',
] as const;
export type DocKind = (typeof DOC_KINDS)[number];

export interface DocKindDef extends Labelled<DocKind> {
  description: string;
  /** هل لهذا النوع تاريخُ انتهاءٍ بطبعه — فيُطلب في النموذج. */
  expires: boolean;
  /** هل له رقمٌ يُكتب. */
  numbered: boolean;
}

export const DOC_KIND_DEFS: readonly DocKindDef[] = [
  {
    key: 'NATIONAL_ID',
    label: 'بطاقة الرقم القوميّ',
    kind: 'info',
    description: 'الهويّةُ الأساسيّة — وتنتهي وتُجدَّد.',
    expires: true,
    numbered: true,
  },
  {
    key: 'PASSPORT',
    label: 'جواز السفر',
    kind: 'info',
    description: 'لمن يسافر في العمل أو لغير المواطنين.',
    expires: true,
    numbered: true,
  },
  {
    key: 'WORK_PERMIT',
    label: 'تصريح العمل',
    kind: 'warning',
    description: 'لغير المواطنين — وانتهاؤه يوقف العملَ قانونًا.',
    expires: true,
    numbered: true,
  },
  {
    key: 'RESIDENCY',
    label: 'الإقامة',
    kind: 'warning',
    description: 'تُجدَّد مع تصريح العمل غالبًا.',
    expires: true,
    numbered: true,
  },
  {
    key: 'QUALIFICATION',
    label: 'المؤهّل الدراسيّ',
    kind: 'neutral',
    description: 'شهادةُ التخرّج — ولا تنتهي.',
    expires: false,
    numbered: false,
  },
  {
    key: 'EXPERIENCE',
    label: 'شهادة خبرة',
    kind: 'neutral',
    description: 'من جهةِ عملٍ سابقة.',
    expires: false,
    numbered: false,
  },
  {
    key: 'MEDICAL',
    label: 'الكشف الطبّيّ',
    kind: 'warning',
    description: 'يُطلب عند التعيين، ويُعاد في بعض المهن دوريًّا.',
    expires: true,
    numbered: false,
  },
  {
    key: 'CRIMINAL_RECORD',
    label: 'صحيفة الحالة الجنائيّة',
    kind: 'neutral',
    description: 'تُطلب عند التعيين ولها صلاحيّةٌ قصيرة.',
    expires: true,
    numbered: false,
  },
  {
    key: 'MILITARY',
    label: 'موقف التجنيد',
    kind: 'neutral',
    description: 'لمن يلزمه.',
    expires: false,
    numbered: false,
  },
  {
    key: 'INSURANCE_FORM',
    label: 'استمارة التأمينات',
    kind: 'info',
    description: 'الاستمارةُ المودَعة لدى التأمينات.',
    expires: false,
    numbered: true,
  },
  {
    key: 'BANK',
    label: 'بيانات الحساب البنكيّ',
    kind: 'neutral',
    description: 'يُحوَّل عليه المرتّب.',
    expires: false,
    numbered: true,
  },
  {
    key: 'PHOTO',
    label: 'صورة شخصيّة',
    kind: 'neutral',
    description: 'للبطاقة التعريفيّة.',
    expires: false,
    numbered: false,
  },
  {
    key: 'OTHER',
    label: 'أخرى',
    kind: 'neutral',
    description: 'ما لا يقع في الأنواع أعلاه — ويُكتب عنوانُه.',
    expires: false,
    numbered: false,
  },
] as const;

export const DOC_KIND_MAP = Object.fromEntries(
  DOC_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DocKind, DocKindDef>>;

/**
 * ما تطلبه الشركةُ افتراضًا.
 *
 * يُقترَح ولا يُفرض: ما يلزم في مصرَ غيرُ ما يلزم في الخليج، والقائمةُ
 * الفعليّةُ إعدادُ شركةٍ يُحفظ في إعداداتها. وهذه بدايةٌ تُعدَّل.
 */
export const DEFAULT_REQUIRED_DOCS: readonly DocKind[] = [
  'NATIONAL_ID',
  'QUALIFICATION',
  'MEDICAL',
] as const;

/* ==========================================================================
   الانتهاء
   ========================================================================== */

/** كم يومًا قبل الانتهاء يُنبَّه — شهرٌ يكفي لتجديدِ ورقةٍ حكوميّة. */
export const DOC_WARN_DAYS = 30;

export type DocState = 'VALID' | 'DUE' | 'EXPIRED' | 'FOREVER';

export interface DocStateDef extends Labelled<DocState> {
  description: string;
}

export const DOC_STATE_DEFS: readonly DocStateDef[] = [
  {
    key: 'VALID',
    label: 'سارٍ',
    kind: 'success',
    description: 'باقٍ على انتهائه متّسع.',
  },
  {
    key: 'DUE',
    label: 'ينتهي قريبًا',
    kind: 'warning',
    description: `خلال ${DOC_WARN_DAYS} يومًا أو أقلّ.`,
  },
  {
    key: 'EXPIRED',
    label: 'منتهٍ',
    kind: 'danger',
    description: 'مضى تاريخُه — ومن يعمل بورقةٍ منتهيةٍ يعمل بلا ورقة.',
  },
  {
    key: 'FOREVER',
    label: 'لا ينتهي',
    kind: 'neutral',
    description: 'ورقةٌ لا تاريخَ لانتهائها كشهادةِ تخرّج.',
  },
] as const;

export const DOC_STATE_MAP = Object.fromEntries(
  DOC_STATE_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DocState, DocStateDef>>;

/**
 * حالةُ ورقةٍ من تاريخ انتهائها.
 *
 * تُحسب في الخادم لا في الشاشة: ثلاثُ شاشاتٍ تعرض المستندات، وحسابُها في
 * كلٍّ منها يجعل لثلاثتها تعريفاتٍ تتباعد — فتقول واحدةٌ «ينتهي قريبًا»
 * وأخرى «سارٍ» لليوم نفسِه.
 */
export function docState(expiresOn: string | null): DocState {
  if (!expiresOn) return 'FOREVER';
  const at = new Date(`${expiresOn}T00:00:00.000Z`).getTime();
  const days = Math.ceil((at - Date.now()) / 86_400_000);
  if (days < 0) return 'EXPIRED';
  if (days <= DOC_WARN_DAYS) return 'DUE';
  return 'VALID';
}

/** الأيّامُ الباقية — سالبُها ما مضى على انتهائه. */
export function docDaysLeft(expiresOn: string | null): number | null {
  if (!expiresOn) return null;
  const at = new Date(`${expiresOn}T00:00:00.000Z`).getTime();
  return Math.ceil((at - Date.now()) / 86_400_000);
}
